import React, { useState, useEffect } from 'react';
import { Box, Text, VStack, Image, HStack, Link, Divider } from '@chakra-ui/react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Gallery from '../components/Gallery';
import NewsletterSignup from '../components/NewsletterSignup';
import { getBlogPostBySlug } from '../data/blogPosts';
import { loadBlogPost, BlogPostContent } from '../utils/markdownLoader';
const BlogPost = () => {
  const { t } = useTranslation('blog');
  const { slug, lng } = useParams<{ slug: string; lng: string }>();
  const navigate = useNavigate();
  const currentLanguage = lng || 'en';
  const post = slug ? getBlogPostBySlug(slug) : null;
  
  const [markdownContent, setMarkdownContent] = useState<BlogPostContent | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const getLocalizedPath = (path: string) => {
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `/${currentLanguage}${cleanPath}`;
  };

  // Load markdown content
  useEffect(() => {
    if (!slug) return;
    
    setIsLoading(true);
    loadBlogPost(slug, currentLanguage)
      .then(content => {
        setMarkdownContent(content);
        setIsLoading(false);
      })
      .catch(() => {
        setMarkdownContent(null);
        setIsLoading(false);
      });
  }, [slug, currentLanguage]);

  // Get content - now primarily from markdown
  const getPostContent = () => {
    if (markdownContent) {
      return markdownContent.content;
    }
    
    // Fallback to original content from blogPosts.ts (for backward compatibility)
    if (post) {
      return post.content;
    }
    
    return [];
  };

  // Get post metadata with markdown fallback
  const getPostMeta = () => {
    if (markdownContent) {
      return {
        title: markdownContent.meta.title || 'Untitled',
        excerpt: markdownContent.meta.excerpt || '',
        date: markdownContent.meta.date || '',
        readTime: markdownContent.meta.readTime || '5 min read',
        image: markdownContent.meta.image || '/images/about.jpg'
      };
    }
    
    if (!post) return null;
    
    return {
      title: t(`posts.${post.id}.title`),
      excerpt: t(`posts.${post.id}.excerpt`),
      date: post.date,
      readTime: post.readTime,
      image: post.image
    };
  };

  if (!post && !markdownContent) {
    return (
      <Box maxW="800px" mx="auto" p={8} textAlign="center">
        <Text fontSize="2xl" fontFamily="Abel">Blog post not found</Text>
        <Link onClick={() => navigate(getLocalizedPath("/blog"))}
          cursor="pointer" fontFamily="Source Code Pro" color="blue.600">
          {t('navigation.backToBlog')}
        </Link>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box maxW="800px" mx="auto" p={8} textAlign="center">
        <Text fontSize="xl" fontFamily="Abel">Loading...</Text>
      </Box>
    );
  }

  const postMeta = getPostMeta();
  if (!postMeta) {
    return (
      <Box maxW="800px" mx="auto" p={8} textAlign="center">
        <Text fontSize="2xl" fontFamily="Abel">Blog post not found</Text>
        <Link onClick={() => navigate(getLocalizedPath("/blog"))}
          cursor="pointer" fontFamily="Source Code Pro" color="blue.600">
          {t('navigation.backToBlog')}
        </Link>
      </Box>
    );
  }

  return (
    <Box maxW="800px" mx="auto" p={8} bg="white" minH="100vh">
      {/* Back to Blog */}
      <Box mb={8}>
        <Link 
          onClick={() => navigate(getLocalizedPath("/blog"))}
          cursor="pointer" 
          fontFamily="Source Code Pro" 
          color="black"
          _hover={{ textDecoration: 'underline' }}
        >
{t('navigation.backToBlog')}
        </Link>
      </Box>

      {/* Hero Image */}
      <Box border="4px solid black" mb={8}>
        <Image
          src={postMeta.image}
          alt={postMeta.title}
          w="100%"
          h={{ base: "400px", md: "500px", lg: "600px" }}
          objectFit="cover"
          loading="lazy"
        />
      </Box>

      {/* Article Header */}
      <VStack spacing={4} align="start" mb={8}>
        <HStack spacing={4}>
          <Text fontSize="sm" color="black" fontFamily="Source Code Pro">
            {postMeta.date}
          </Text>
          <Text fontSize="sm" color="black" fontFamily="Source Code Pro">
            {t('metadata.readTime', { time: postMeta.readTime?.replace(' min read', '') || '5' })}
          </Text>
        </HStack>
        
        <Text 
          fontSize={{ base: "2xl", md: "4xl" }} 
          fontWeight="bold" 
          color="black"
          fontFamily="Abel"
          lineHeight="1.2"
        >
          {postMeta.title}
        </Text>
        
        <Text 
          fontSize="lg" 
          color="black" 
          lineHeight="1.6"
          fontFamily="Source Code Pro"
          fontStyle="italic"
        >
          {postMeta.excerpt}
        </Text>
      </VStack>

      <Divider borderColor="black" borderWidth="1px" mb={8} />

      {/* Article Content */}
      <VStack spacing={6} align="start">
        {getPostContent().map((paragraph, index) => {
          if (paragraph === 'GALLERY_PLACEHOLDER_1') {
            // First gallery - Pure joy expressions
            if (slug === 'surf-expression-photography-pure-joy') {
              const surfGallery1 = [
                { 
                  src: '/images/expression_surf/M10M0948.jpg', 
                  alt: 'Pure surfing joy - Authentic happiness captured on analog film' 
                },
                { 
                  src: '/images/expression_surf/M10M0965.jpg', 
                  alt: 'Spontaneous surf celebration - Unguarded moment of pure fun' 
                }
              ];
              
              return (
                <Box key={index} w="100%" my={8}>
                  <Gallery 
                    images={surfGallery1} 
                    columns={{ base: 1, md: 2, lg: 2 }}
                  />
                </Box>
              );
            }
          }
          
          if (paragraph === 'GALLERY_PLACEHOLDER_2') {
            // Second gallery - Genuine expressions
            if (slug === 'surf-expression-photography-pure-joy') {
              const surfGallery2 = [
                { 
                  src: '/images/expression_surf/M10M0995.jpg', 
                  alt: 'Genuine surf happiness - Film photography capturing real emotions' 
                },
                { 
                  src: '/images/expression_surf/Entre_Olas_8.jpg', 
                  alt: 'Post-wave euphoria - Natural expression of surfing stoke' 
                }
              ];
              
              return (
                <Box key={index} w="100%" my={8}>
                  <Gallery 
                    images={surfGallery2} 
                    columns={{ base: 1, md: 2, lg: 2 }}
                  />
                </Box>
              );
            }
          }
          
          if (paragraph === 'GALLERY_PLACEHOLDER_3') {
            // Third gallery - Authentic moments
            if (slug === 'surf-expression-photography-pure-joy') {
              const surfGallery3 = [
                { 
                  src: '/images/expression_surf/M10M1032.jpg', 
                  alt: 'Authentic surf laughter - Adults becoming kids in the water' 
                },
                { 
                  src: '/images/expression_surf/ML1005609.jpg', 
                  alt: 'Pure surfing bliss - Real moments of joy on analog film' 
                }
              ];
              
              return (
                <Box key={index} w="100%" my={8}>
                  <Gallery 
                    images={surfGallery3} 
                    columns={{ base: 1, md: 2, lg: 2 }}
                  />
                </Box>
              );
            }
          }
          
          if (paragraph === 'GALLERY_PLACEHOLDER_BREATHING') {
            // Gallery a) wim1-2 breathing session
            const breathingImages = [
              { 
                src: '/images/wim/wim1.jpg', 
                alt: 'Wim Hof breathing workshop preparation - Shot on Kodak Gold with Leica M6 in Tarifa' 
              },
              { 
                src: '/images/wim/wim2.jpg', 
                alt: 'Deep breathing session participants - Analog film photography Tarifa workshop' 
              }
            ];
            
            return (
              <Box key={index} w="100%" my={8}>
                <Gallery 
                  images={breathingImages} 
                  columns={{ base: 1, md: 2, lg: 2 }}
                />
              </Box>
            );
          }

          if (paragraph === 'GALLERY_PLACEHOLDER_ICE_PREP') {
            // Gallery c) wim4 and wim6
            const icePrepImages = [
              { 
                src: '/images/wim/wim4.jpg', 
                alt: 'Ice bath preparation and mindset - Kodak Gold film captures warm tones in Tarifa' 
              },
              { 
                src: '/images/wim/wim6.jpg', 
                alt: 'Contemplative moments before ice bath - Leica M6 with 28mm Summilux' 
              }
            ];
            
            return (
              <Box key={index} w="100%" my={8}>
                <Gallery 
                  images={icePrepImages} 
                  columns={{ base: 1, md: 2, lg: 2 }}
                />
              </Box>
            );
          }

          if (paragraph === 'GALLERY_PLACEHOLDER_WIM5') {
            // Gallery d) wim5 only
            const wim5Images = [
              { 
                src: '/images/wim/wim5.jpg', 
                alt: 'Participants preparing for cold exposure - Film photography Wim Hof workshop' 
              }
            ];
            
            return (
              <Box key={index} w="100%" my={8}>
                <Gallery 
                  images={wim5Images} 
                  columns={{ base: 1, md: 1, lg: 1 }}
                />
              </Box>
            );
          }

          if (paragraph === 'GALLERY_PLACEHOLDER_MAIN_SESSION') {
            // Gallery d) wim7-9
            const mainSessionImages = [
              { 
                src: '/images/wim/wim7.jpg', 
                alt: 'Community support during cold therapy preparation - Analog film Tarifa' 
              },
              { 
                src: '/images/wim/wim8.jpg', 
                alt: 'Main breathing session in full swing - Kodak Gold captures golden hour light' 
              },
              { 
                src: '/images/wim/wim9.jpg', 
                alt: 'Intense breathwork moment - Film grain adds texture to transformative experience' 
              }
            ];
            
            return (
              <Box key={index} w="100%" my={8}>
                <Gallery 
                  images={mainSessionImages} 
                  columns={{ base: 1, md: 2, lg: 3 }}
                />
              </Box>
            );
          }

          if (paragraph === 'GALLERY_PLACEHOLDER_FINAL_MOMENT') {
            // Gallery e) wim10 only
            const finalMomentImages = [
              { 
                src: '/images/wim/wim10.jpg', 
                alt: 'Workshop community and connection - Analog photography preserves authentic moments' 
              }
            ];
            
            return (
              <Box key={index} w="100%" my={8}>
                <Gallery 
                  images={finalMomentImages} 
                  columns={{ base: 1, md: 1, lg: 1 }}
                />
              </Box>
            );
          }

          if (paragraph === 'GALLERY_PLACEHOLDER_AMSTERDAM') {
            // Gallery f) amster1-3
            const amsterdamImages = [
              { 
                src: '/images/wim/amster1.jpg', 
                alt: 'Amsterdam Wim Hof workshop - Shot on Tri-X black and white film with Leica M6' 
              },
              { 
                src: '/images/wim/amster2.jpg', 
                alt: 'Documentary style breathwork session Amsterdam - Tri-X film grain captures raw emotion' 
              },
              { 
                src: '/images/wim/amster3.jpg', 
                alt: 'Black and white ice bath preparation Amsterdam - Analog film documentary photography' 
              }
            ];
            
            return (
              <Box key={index} w="100%" my={8}>
                <Gallery 
                  images={amsterdamImages} 
                  columns={{ base: 1, md: 2, lg: 3 }}
                />
              </Box>
            );
          }
          
          if (paragraph === 'GALLERY_PLACEHOLDER_YOW_INTRO') {
            // Gallery for YOW intro section
            const yowIntroImages = [
              { 
                src: '/images/yow/yow3.jpg', 
                alt: 'YOW Surfskates brand aesthetic - Film photography in action sports marketing' 
              },
              { 
                src: '/images/yow/yow2.jpg', 
                alt: 'Analog film grain texture - Authentic visual identity in skateboarding' 
              }
            ];
            
            return (
              <Box key={index} w="100%" my={8}>
                <Gallery 
                  images={yowIntroImages} 
                  columns={{ base: 1, md: 2, lg: 2 }}
                />
              </Box>
            );
          }

          if (paragraph === 'GALLERY_PLACEHOLDER_YOW_APPROACH') {
            // Gallery for YOW approach section
            const yowApproachImages = [
              { 
                src: '/images/yow/yow4.jpg', 
                alt: 'Film photography intentionality - Every frame counts in analog skateboarding' 
              },
              { 
                src: '/images/yow/yow5.jpg', 
                alt: 'Authentic brand positioning through analog photography choices' 
              }
            ];
            
            return (
              <Box key={index} w="100%" my={8}>
                <Gallery 
                  images={yowApproachImages} 
                  columns={{ base: 1, md: 2, lg: 2 }}
                />
              </Box>
            );
          }

          if (paragraph === 'GALLERY_PLACEHOLDER_YOW') {
            // Gallery for YOW brand analysis
            const yowImages = [
              { 
                src: '/images/yow/yow6.jpg', 
                alt: 'Authentic skateboarding moments - Film photography brand differentiation' 
              },
              { 
                src: '/images/yow/yow7.jpg', 
                alt: 'YOW brand identity through analog visual language' 
              }
            ];
            
            return (
              <Box key={index} w="100%" my={8}>
                <Gallery 
                  images={yowImages} 
                  columns={{ base: 1, md: 2, lg: 2 }}
                />
              </Box>
            );
          }

          if (paragraph === 'GALLERY_PLACEHOLDER_YOW_FINAL') {
            // Gallery for YOW final section
            const yowFinalImages = [
              {
                src: '/images/yow/yow9.jpg',
                alt: 'YOW surfskate culture - Film photography captures authentic brand essence'
              },
              {
                src: '/images/yow/yow10.png',
                alt: 'Analog photography in surfskate marketing - YOW brand storytelling'
              }
            ];

            return (
              <Box key={index} w="100%" my={8}>
                <Gallery
                  images={yowFinalImages}
                  columns={{ base: 1, md: 2, lg: 2 }}
                />
              </Box>
            );
          }

          if (paragraph === 'GALLERY_PLACEHOLDER_BOLONIA_1') {
            // First Bolonia gallery - Most dramatic lunar dunes (max 2 images)
            const bolonia1Images = [
              {
                src: '/images/bolonia_dunes/05.jpg',
                alt: 'Lunar landscape aesthetic - Bolonia dunes with dramatic sky contrast created by red filter'
              },
              {
                src: '/images/bolonia_dunes/07.jpg',
                alt: 'Minimalist dune composition - White sand against black sky, Leica M3 with red filter'
              }
            ];

            return (
              <Box key={index} w="100%" my={8}>
                <Gallery
                  images={bolonia1Images}
                  columns={{ base: 1, md: 2, lg: 2 }}
                />
              </Box>
            );
          }

          if (paragraph === 'GALLERY_PLACEHOLDER_BOLONIA_2') {
            // Second Bolonia gallery - Single abstract dune texture
            const bolonia2Images = [
              {
                src: '/images/bolonia_dunes/04.jpg',
                alt: 'Sand ripple patterns and shadows - Abstract landscape photography on film'
              }
            ];

            return (
              <Box key={index} w="100%" my={8}>
                <Gallery
                  images={bolonia2Images}
                  columns={{ base: 1, md: 1, lg: 1 }}
                />
              </Box>
            );
          }

          if (paragraph === 'GALLERY_PLACEHOLDER_BOLONIA_3') {
            // Third Bolonia gallery - Wind-carved textures
            const bolonia3Images = [
              {
                src: '/images/bolonia_dunes/06.jpg',
                alt: 'Wind-carved sand textures - Close-up of Bolonia dune patterns on Fomapan 100'
              }
            ];

            return (
              <Box key={index} w="100%" my={8}>
                <Gallery
                  images={bolonia3Images}
                  columns={{ base: 1, md: 1, lg: 1 }}
                />
              </Box>
            );
          }

          if (paragraph === 'GALLERY_PLACEHOLDER_BOLONIA_4') {
            // Fourth Bolonia gallery - The happy accident (image 02)
            const bolonia4Images = [
              {
                src: '/images/bolonia_dunes/02.jpg',
                alt: 'Happy darkroom accident - Development mark creates crescent moon effect in sky above driftwood'
              }
            ];

            return (
              <Box key={index} w="100%" my={8}>
                <Gallery
                  images={bolonia4Images}
                  columns={{ base: 1, md: 1, lg: 1 }}
                />
              </Box>
            );
          }

          if (paragraph === 'GALLERY_PLACEHOLDER_BOLONIA_5') {
            // Fifth Bolonia gallery - More abstract lunar dune (image 03)
            const bolonia5Images = [
              {
                src: '/images/bolonia_dunes/03.jpg',
                alt: 'Abstract dune landscape - Minimalist composition on Fomapan 100 pushed to 400'
              }
            ];

            return (
              <Box key={index} w="100%" my={8}>
                <Gallery
                  images={bolonia5Images}
                  columns={{ base: 1, md: 1, lg: 1 }}
                />
              </Box>
            );
          }

          if (paragraph === 'GALLERY_PLACEHOLDER_BOLONIA_6') {
            // Sixth Bolonia gallery - Abstract driftwood on black (image 01)
            const bolonia6Images = [
              {
                src: '/images/bolonia_dunes/01.jpg',
                alt: 'Bleached driftwood emerging from deep black shadows - High contrast abstract composition on pushed film'
              }
            ];

            return (
              <Box key={index} w="100%" my={8}>
                <Gallery
                  images={bolonia6Images}
                  columns={{ base: 1, md: 1, lg: 1 }}
                />
              </Box>
            );
          }

          if (paragraph === 'GALLERY_PLACEHOLDER_BOLONIA_7') {
            // Seventh Bolonia gallery - FINAL: Driftwood with moons (08 and 09 together)
            const bolonia7Images = [
              {
                src: '/images/bolonia_dunes/08.jpg',
                alt: 'Bleached driftwood with actual moon visible - Dual celestial elements on black and white film'
              },
              {
                src: '/images/bolonia_dunes/09.jpg',
                alt: 'High contrast driftwood silhouette - Nearly negative space composition on pushed film'
              }
            ];

            return (
              <Box key={index} w="100%" my={8}>
                <Gallery
                  images={bolonia7Images}
                  columns={{ base: 1, md: 2, lg: 2 }}
                />
              </Box>
            );
          }

          if (paragraph === 'GALLERY_PLACEHOLDER_BOLONIA_8') {
            // Eighth Bolonia gallery - BTS: Behind the scenes mobile photos
            const bolonia8Images = [
              {
                src: '/images/bolonia_dunes/bts_01.jpg',
                alt: 'Behind the scenes - Capturing the dunes with Leica M3 and red filter at Bolonia'
              },
              {
                src: '/images/bolonia_dunes/bts_02.jpg',
                alt: 'Behind the scenes - Shooting film photography in the Bolonia dunes at sunrise'
              }
            ];

            return (
              <Box key={index} w="100%" my={8}>
                <Gallery
                  images={bolonia8Images}
                  columns={{ base: 1, md: 2, lg: 2 }}
                />
              </Box>
            );
          }

          if (paragraph === 'VIDEO_PLACEHOLDER') {
            return (
              <Box key={index} w="100%" my={8}>
                <Box 
                  position="relative" 
                  paddingBottom="56.25%" 
                  height="0" 
                  overflow="hidden"
                  border="4px solid black"
                >
                  <iframe
                    src="https://www.youtube.com/embed/-Kw0xyEQ2eM"
                    title="Entre Olas - Film Photography Project"
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%'
                    }}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </Box>
              </Box>
            );
          }
          
          // Parse markdown-style links [text](url)
          const parseLinks = (text: string) => {
            const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
            const parts = [];
            let lastIndex = 0;
            let match;

            while ((match = linkRegex.exec(text)) !== null) {
              // Add text before the link
              if (match.index > lastIndex) {
                parts.push(text.slice(lastIndex, match.index));
              }
              
              // Add the link
              parts.push({
                type: 'link',
                text: match[1],
                url: match[2]
              });
              
              lastIndex = match.index + match[0].length;
            }
            
            // Add remaining text
            if (lastIndex < text.length) {
              parts.push(text.slice(lastIndex));
            }
            
            return parts;
          };

          const hasLinks = paragraph.includes('[') && paragraph.includes('](');
          if (hasLinks) {
            const parts = parseLinks(paragraph);
            return (
              <Text 
                key={index}
                fontSize="md" 
                color="black" 
                lineHeight="1.8"
                fontFamily="Source Code Pro"
              >
                {parts.map((part: any, partIndex: number) => {
                  if (typeof part === 'object' && part.type === 'link') {
                    return (
                      <Link 
                        key={partIndex}
                        href={part.url}
                        isExternal 
                        color="black" 
                        textDecoration="underline"
                        _hover={{ color: "gray.600" }}
                      >
                        {part.text}
                      </Link>
                    );
                  }
                  return part;
                })}
              </Text>
            );
          }

          return (
            <Text 
              key={index}
              fontSize="md" 
              color="black" 
              lineHeight="1.8"
              fontFamily="Source Code Pro"
            >
              {paragraph}
            </Text>
          );
        })}
      </VStack>

      <Divider borderColor="black" borderWidth="1px" my={12} />

      {/* Newsletter Signup */}
      <Box mt={12} maxW="400px" mx="auto">
        <NewsletterSignup 
          source="blog-post"
          title={t('newsletter.title')}
          description={t('newsletter.description')}
          variant="inline"
        />
      </Box>

      {/* Author & Contact */}
      <Box 
        textAlign="center" 
        p={8} 
        bg="black"
        border="4px solid black"
        color="white"
        mt={12}
      >
        <Text 
          fontSize="xl" 
          fontWeight="bold" 
          mb={4}
          fontFamily="Abel"
          color="white"
        >
{t('author.aboutTitle')}
        </Text>
        <Text 
          fontSize="md" 
          mb={6} 
          color="white"
          lineHeight="1.6"
          fontFamily="Source Code Pro"
        >
{t('author.description')}
        </Text>
        <VStack spacing={2}>
          <Link 
            onClick={() => navigate(getLocalizedPath("/services"))}
            cursor="pointer" 
            color="white" 
            fontFamily="Source Code Pro"
            _hover={{ textDecoration: 'underline' }}
          >
{t('author.viewServices')}
          </Link>
          <Link 
            href="mailto:algranoconpablo@gmail.com" 
            color="white" 
            fontFamily="Source Code Pro"
            _hover={{ textDecoration: 'underline' }}
          >
{t('author.contactBooking')}
          </Link>
        </VStack>
      </Box>

    </Box>
  );
};

export default BlogPost;